import { Action } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { put, takeLatest } from 'redux-saga/effects';
import { UserModel } from '../models/UserModel';

export interface ActionWithPayload<T> extends Action {
	payload?: T;
}

export const actionTypes = {
	Login: '[Login] Action',
	Logout: '[Logout] Action',
	Register: '[Register] Action',
	UserRequested: '[Request User] Action',
	UserLoaded: '[Load User] Auth API',
	SetUser: '[Set User] Action',
	Redirect: '[Redirect] Action',
	Refresh: '[Refresh] Action',
};

const initialAuthState: IAuthState = {};

export interface IAuthState {
	userData?: UserModel;
	accessToken?: string;
	idToken?: string;
	returnUrl?: string;
}

export const reducer = persistReducer(
	{ storage, key: 'v100-demo1-auth', whitelist: ['userData', 'accessToken', 'idToken', 'userId'] },
	(state: IAuthState = initialAuthState, action: ActionWithPayload<IAuthState>) => {
		switch (action.type) {
			case actionTypes.Login: {
				const accessToken = action.payload?.accessToken;
				const idToken = action.payload?.idToken;
				const userData = action.payload?.userData;
				return { accessToken, idToken, userData };
			}

			case actionTypes.Register: {
				const accessToken = action.payload?.accessToken;
				const idToken = action.payload?.idToken;
				const userData = action.payload?.userData;
				return { accessToken, idToken, userData };
			}

			case actionTypes.Logout: {
				const returnUrl = action.payload?.returnUrl;
				return { ...initialAuthState, returnUrl };
			}

			case actionTypes.UserRequested: {
				return { ...state };
			}

			case actionTypes.UserLoaded: {
				const userData = action.payload?.userData;
				return { ...state, userData };
			}

			case actionTypes.SetUser: {
				const userData = action.payload?.userData;
				return { ...state, userData };
			}

			case actionTypes.Redirect: {
				const returnUrl = action.payload?.returnUrl;
				return { ...state, returnUrl };
			}

			default:
				return state;
		}
	}
);

interface ILoginProps {
	accessToken: string;
	idToken: string;
	userData: UserModel;
}

interface ILogin {
	payload: ILoginProps;
	type: string;
}

interface IRegisterProps {
	accessToken: string;
	idToken: string;
}

interface IRegister {
	payload: IRegisterProps;
	type: string;
}

interface IUser {
	payload: {
		userData: UserModel
	},
	type: string
}

interface IRedirect {
	payload: {
		returnUrl: string
	},
	type: string
}

// const expiredToken = 'eyJraWQiOiJoXC9oRjRySzJSS3NURlBLeXpYOWNjdkVQVEZIbTcxKzBcL1UyMWN3QnRJUHc9IiwiYWxnIjoiUlMyNTYifQ.eyJzdWIiOiJhNTYwMWMyYy1mZmJjLTQ0YzQtODcyYi1mMWU3YWUzMWEzYTMiLCJjb2duaXRvOmdyb3VwcyI6WyJTVVBFUl9BRE1JTiIsIkFETUlOIl0sImVtYWlsX3ZlcmlmaWVkIjp0cnVlLCJpc3MiOiJodHRwczpcL1wvY29nbml0by1pZHAudXMtZWFzdC0xLmFtYXpvbmF3cy5jb21cL3VzLWVhc3QtMV9yQ1VwVGdYWTQiLCJwaG9uZV9udW1iZXJfdmVyaWZpZWQiOmZhbHNlLCJjb2duaXRvOnVzZXJuYW1lIjoiYTU2MDFjMmMtZmZiYy00NGM0LTg3MmItZjFlN2FlMzFhM2EzIiwiZ2l2ZW5fbmFtZSI6Illhcm9zbGF2Iiwib3JpZ2luX2p0aSI6IjczMWFmYTUzLWZmMzktNGVkYS1iNjg5LWRmNjI1NDNjYzk0YyIsImNvZ25pdG86cm9sZXMiOlsiYXJuOmF3czppYW06OjU1NjY2MzAxMDg3MTpyb2xlXC9GQ19BRE1JTiJdLCJhdWQiOiI0bjBpOGE0aTlvMXZrM2czdGY2NG82YmxnNyIsImV2ZW50X2lkIjoiMzkxZTg2MzktZDAzYS00MTYxLWIyZjMtMWVlM2IzOGRmNjBmIiwidG9rZW5fdXNlIjoiaWQiLCJhdXRoX3RpbWUiOjE3MDMyMzA3MDcsInBob25lX251bWJlciI6IisxMjM0NTY3ODkwOSIsImV4cCI6MTcwMzIzNzkwNywiaWF0IjoxNzAzMjMwNzA3LCJmYW1pbHlfbmFtZSI6IktvY2h1cm92IiwianRpIjoiNTI1Yzc3NmEtNzM3ZS00MjNhLTg4NzYtNDk3YjhlMWNlMjhkIiwiZW1haWwiOiJZYXJvc2xhdi5Lb2NodXJvdkBmbGV4LWNoYXJnZS5jb20ifQ.rOIhUP1pUdmLsW12um3rz3wZDFjqOqCRWWi_aC7b7_DQ6xf_pVppAq2PnoSs8kXXKRwwchOTONMMGllrcUR6d8Qhtro7KYFe3R8D1POLndmT2aoFexGl89DtjD9uPuJsneh-e2xRfpFVDC2Wpg9AqHH8SDt4oG586IKFG7xQpAZoPkFvv3SClvXzo4gGNGV-AnmFOvk4DQ5qPOZodCrdAwS7gs0Xg-cAp3T3T1DuUh_yj0rQEyV58Mul-ljGXpuu60VGkmF3qpbii8YvKxKOrhf77-4QvT_eXzslBVCk9oYtxVG5FrizTKly1FfrIUaf_yIuvM2QOYkzRDbtzQoYTw';

export const actions = {
	login: ({ accessToken, idToken, userData }: ILoginProps): ILogin => {
		localStorage.setItem('idToken', idToken);
		localStorage.setItem('accessToken', accessToken);
		return ({
			type: actionTypes.Login,
			payload: {
				accessToken,
				// idToken: expiredToken,
				idToken,
				userData
			}
		});
	},
	register: ({ accessToken, idToken }: IRegisterProps): IRegister => ({
		type: actionTypes.Register,
		payload: { accessToken, idToken }
	}),
	logout: (returnUrl?: string): { payload: { returnUrl: string }, type: string } => {
		localStorage.removeItem('idToken');
		localStorage.removeItem('accessToken');
		return({
			type: actionTypes.Logout,
			payload: { returnUrl: returnUrl || '/auth/login' }
		});
	},
	requestUser: (): { type: string } => ({
		type: actionTypes.UserRequested
	}),
	fulfillUser: (userData: UserModel): IUser => ({ type: actionTypes.UserLoaded, payload: { userData }}),
	setUser: (userData: UserModel): IUser => ({ type: actionTypes.SetUser, payload: { userData }}),
	redirect: (returnUrl: string): IRedirect => ({ type: actionTypes.Redirect, payload: { returnUrl }})
};

export function* saga(): any {
	yield takeLatest(actionTypes.Login, function* loginSaga() {
		yield put(actions.requestUser());
	});

	yield takeLatest(actionTypes.Register, function* registerSaga() {
		yield put(actions.requestUser());
	});
}
